import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Search } from "lucide-react";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { useRuns, useAgentRuns, useStopRun, useStopAgentRun } from "@/hooks/reports";
import type { RunStatusValue } from "@/services/reports";

type StatusFilter = "all" | "running" | "done" | "stopped" | "error" | "cached";
type SurfaceFilter = "all" | "playbook" | "agent";

type ReportRow = {
  id: string;
  surface: "playbook" | "agent";
  surfaceId: string;
  title: string;
  slug: string;
  category: string;
  targetName?: string | null;
  targetType?: string | null;
  status: RunStatusValue;
  total_ms?: number | null;
  startedAt?: string | null;
  href: string;
};

const STATUS_FILTERS: Array<{ id: StatusFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "running", label: "Running" },
  { id: "done", label: "Done" },
  { id: "cached", label: "Cached" },
  { id: "stopped", label: "Stopped" },
  { id: "error", label: "Error" },
];

const SURFACE_TABS: Array<{ id: SurfaceFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "playbook", label: "Migrations" },
  { id: "agent", label: "Agents" },
];

function durationLabel(totalMs: number | null | undefined): string {
  if (!totalMs || !Number.isFinite(totalMs) || totalMs <= 0) return "-";
  if (totalMs < 1000) return `${Math.round(totalMs)}ms`;
  return `${Math.round(totalMs / 1000)}s`;
}

function StatusDot({ status }: { status: string }) {
  if (status === "running") {
    return (
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
      </span>
    );
  }
  if (status === "done" || status === "cached") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--success)/0.6)]" />;
  }
  if (status === "error" || status === "stopped") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--destructive)/0.7)]" />;
  }
  return <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/20" />;
}

const RUNS_PAGE_SIZE = 60;
const MAX_RUN_LIMIT = 500;

export function RunsList() {
  const [limit, setLimit] = useState(RUNS_PAGE_SIZE);
  const playbookRunsQ = useRuns({ limit });
  const agentRunsQ = useAgentRuns({ limit });
  const stopRun = useStopRun();
  const stopAgentRun = useStopAgentRun();

  const [surfaceTab, setSurfaceTab] = useState<SurfaceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");

  const reportRows = useMemo<ReportRow[]>(() => {
    const playbookRows: ReportRow[] = (playbookRunsQ.data?.runs ?? []).map((run) => ({
      id: run.id,
      surface: "playbook",
      surfaceId: run.skill_id,
      title: run.surface_name || run.skill_id,
      slug: run.surface_slug || run.skill_id,
      category: run.surface_category || "playbook",
      targetName: run.target_name,
      targetType: run.target_type,
      status: run.status,
      total_ms: run.total_ms,
      startedAt: run.started_at,
      // Open in the migration workspace's Run tab report view (same surface as
      // its Run History → "View report"), not the standalone detail page.
      href: `/migrations/${encodeURIComponent(run.skill_id)}?run_id=${encodeURIComponent(run.id)}`,
    }));
    const agentRows: ReportRow[] = (agentRunsQ.data?.runs ?? []).map((run) => ({
      id: run.id,
      surface: "agent",
      surfaceId: run.agent_id,
      title: run.surface_name || run.agent_id,
      slug: run.surface_slug || run.agent_id,
      category: run.surface_type || "agent",
      targetName: run.target_name,
      targetType: run.target_type,
      status: run.status,
      total_ms: run.total_ms,
      startedAt: run.started_at,
      // Open in the agent workspace's Run tab report view (same surface as
      // Run History → "View report"), not the standalone detail page.
      href: `/agents/${encodeURIComponent(run.agent_id)}?run_id=${encodeURIComponent(run.id)}`,
    }));
    // Most-recent run first (ISO timestamps sort lexically). Was sorting by the
    // random run UUID, which produced an effectively arbitrary order.
    return [...playbookRows, ...agentRows].sort(
      (a, b) => (b.startedAt || "").localeCompare(a.startedAt || "")
    );
  }, [agentRunsQ.data?.runs, playbookRunsQ.data?.runs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reportRows.filter((row) => {
      if (surfaceTab !== "all" && row.surface !== surfaceTab) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (q && !row.title.toLowerCase().includes(q) && !row.slug.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [reportRows, statusFilter, surfaceTab, query]);

  const isLoading = playbookRunsQ.isLoading || agentRunsQ.isLoading;
  const isError = playbookRunsQ.isError || agentRunsQ.isError;
  const errorMessage =
    (playbookRunsQ.error as Error | null)?.message ||
    (agentRunsQ.error as Error | null)?.message;

  // Reports merges two independently-limited sources (playbook + agent runs), so
  // pagination grows a shared window instead of cursoring each stream. If EITHER source
  // returned a full window (>= limit) there may be more — keep offering "Load more" until
  // both come back short or we hit the backend cap. Use RAW fetched counts, not the
  // filtered view, since paging is about how much we've loaded.
  const isFetchingMore = playbookRunsQ.isFetching || agentRunsQ.isFetching;
  const rawPlaybookCount = playbookRunsQ.data?.runs?.length ?? 0;
  const rawAgentCount = agentRunsQ.data?.runs?.length ?? 0;
  const hasMore = limit < MAX_RUN_LIMIT && (rawPlaybookCount >= limit || rawAgentCount >= limit);

  const stopRow = (row: ReportRow) => {
    if (row.surface === "agent") stopAgentRun.mutate(row.id);
    else stopRun.mutate(row.id);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Sticky header */}
      <div className="shrink-0 pb-3">
        {/* Title row */}
        <div className="py-1 pb-4">
          <h1 className="text-2xl font-bold tracking-tight">Run History</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            All migration and agent execution runs across your org.
          </p>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 transition-colors focus-within:border-primary/40 focus-within:bg-muted/30">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or slug…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40"
          />
          {reportRows.length > 0 && !query && (
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground/50">
              {filtered.length} runs
            </span>
          )}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="shrink-0 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {filtered.length} found · clear
            </button>
          )}
        </div>

        {/* Surface tabs + status pills */}
        <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          {/* Surface type tabs */}
          <div className="flex border-b border-border/50">
            {SURFACE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSurfaceTab(tab.id)}
                className={[
                  "border-b-2 px-4 py-2.5 text-xs transition-colors",
                  surfaceTab === tab.id
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap items-center gap-1.5 pb-1.5">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setStatusFilter(filter.id)}
                className={[
                  "rounded-full border px-2.5 py-0.5 font-mono text-[11px] transition-colors",
                  statusFilter === filter.id
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card text-foreground border-border hover:bg-muted",
                ].join(" ")}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={["flex items-center gap-4 px-5 py-3.5", i < 4 ? "border-b border-border/60" : ""].join(" ")}>
                <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/15" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-48 rounded-md bg-muted-foreground/10 loading-parallax" />
                  <div className="h-2.5 w-32 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
                </div>
                <div className="h-5 w-16 rounded-full bg-muted-foreground/10 loading-parallax" />
                <div className="h-2.5 w-16 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <p className="font-mono text-sm text-destructive">{errorMessage ?? "Failed to load runs."}</p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-24 text-center">
            <p className="font-semibold">No runs found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {query || statusFilter !== "all" ? "Try adjusting your filters." : "No runs have been recorded yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {filtered.map((row, idx) => (
              <div
                key={`${row.surface}:${row.id}`}
                className={[
                  "group relative flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/25",
                  idx < filtered.length - 1 ? "border-b border-border/60" : "",
                ].join(" ")}
              >
                <StatusDot status={row.status} />

                <Link to={row.href} className="min-w-0 flex-1 focus:outline-none">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                      {row.title}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 overflow-hidden font-mono text-[11px] text-muted-foreground">
                    <span className="capitalize">{row.surface === "playbook" ? "Migration" : row.surface}</span>
                    {row.targetName && row.targetName !== "__all__" && (
                      <>
                        <span className="opacity-30">·</span>
                        <span className="truncate">{row.targetName}</span>
                      </>
                    )}
                  </div>
                </Link>

                <RunStatusBadge status={row.status} />

                <span className="shrink-0 font-mono text-[11px] text-muted-foreground w-12 text-right">
                  {durationLabel(row.total_ms)}
                </span>

                <div className="flex shrink-0 items-center gap-2">
                  {row.status === "running" && (
                    <button
                      type="button"
                      onClick={() => stopRow(row)}
                      className="font-mono text-[11px] text-destructive hover:underline"
                      disabled={stopRun.isPending || stopAgentRun.isPending}
                    >
                      stop
                    </button>
                  )}
                  <Link
                    to={row.href}
                    className="inline-flex items-center gap-1 font-mono text-[11px] text-primary hover:underline"
                  >
                    open <ExternalLink className="h-2.5 w-2.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !isError && filtered.length > 0 && hasMore && (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={() => setLimit((current) => Math.min(current + RUNS_PAGE_SIZE, MAX_RUN_LIMIT))}
              disabled={isFetchingMore}
              className="rounded-md border border-border bg-card px-4 py-2 font-mono text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50"
            >
              {isFetchingMore ? "Loading…" : "Load more runs"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
